import React from 'react';
import Icon from '../../../components/AppIcon';

const SegmentationAnalysis = ({ data }) => {
  if (!data || data?.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border shadow-therapeutic p-6">
        <div className="text-center text-muted-foreground text-sm py-8">
          No segmentation data available
        </div>
      </div>
    );
  }

  const getPerformanceColor = (rate) => {
    if (rate >= 15) return 'text-success';
    if (rate >= 10) return 'text-primary';
    if (rate >= 5) return 'text-warning';
    return 'text-error';
  };

  const getPerformanceBadge = (rate) => {
    if (rate >= 15) return { label: 'High', color: 'bg-success/10 text-success' };
    if (rate >= 10) return { label: 'Good', color: 'bg-primary/10 text-primary' };
    if (rate >= 5) return { label: 'Fair', color: 'bg-warning/10 text-warning' };
    return { label: 'Low', color: 'bg-error/10 text-error' };
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-therapeutic p-6">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="Users" size={20} className="text-primary" strokeWidth={2} />
        <h2 className="text-lg font-heading font-semibold text-foreground">
          Segmentation Analysis
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data?.map((segment) => {
          const badge = getPerformanceBadge(segment?.conversionRate);
          
          return (
            <div key={segment?.segment} className="bg-muted/5 rounded-lg border border-border p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-1">{segment?.segment}</h3>
                  <p className="text-xs text-muted-foreground">{segment?.description}</p>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${badge?.color}`}>
                  {badge?.label}
                </span>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Total Users</span>
                  <span className="text-sm font-medium text-foreground">{segment?.totalUsers?.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Conversions</span>
                  <span className="text-sm font-medium text-foreground">{segment?.conversions?.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Conversion Rate</span>
                  <span className={`text-sm font-semibold ${getPerformanceColor(segment?.conversionRate)}`}>
                    {segment?.conversionRate}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Avg. Time to Convert</span>
                  <span className="text-sm font-medium text-foreground">{segment?.avgTimeToConvert} days</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Avg. Revenue</span>
                  <span className="text-sm font-medium text-foreground">${segment?.avgRevenue}</span>
                </div>
              </div>

              {/* Upgrade Likelihood Indicator */}
              <div className="mt-3 pt-3 border-t border-border">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-muted-foreground">Upgrade Likelihood</span>
                  <span className="text-xs font-medium text-foreground">{segment?.upgradeLikelihood}%</span>
                </div>
                <div className="w-full h-2 bg-muted/20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary to-success transition-all duration-500"
                    style={{ width: `${segment?.upgradeLikelihood}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <Icon name="Info" size={14} strokeWidth={2} className="mt-0.5" />
          <p>
            Segmentation analysis shows conversion performance by user demographics, usage patterns, and behavior. 
            Focus optimization efforts on high-potential segments with low conversion rates.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SegmentationAnalysis;